import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { LocalizedLink as Link } from '../components/LocalizedLink';
import BlogLayout from '../components/BlogLayout';
import SEO from '../components/SEO';
import './AboutPage.css';

export default function AboutPage() {
  const { t } = useTranslation();

  useEffect(() => {
    const els = document.querySelectorAll('.lp-reveal');
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) { e.target.classList.add('visible'); observer.unobserve(e.target); } }),
      { threshold: 0.15, rootMargin: '0px 0px -40px 0px' },
    );
    els.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const team = [
    {
      name: 'Omar Khatib',
      role: t('about.coFounderHeadOfBusiness'),
      bio: t('about.aTechVisionaryWithADeep'),
      image: '/images/team/omar.png',
      linkedin: 'https://www.linkedin.com/in/omar-khatib-01/'
    },
    {
      name: 'Jihad Asad',
      role: t('about.founderCeo'),
      bio: t('about.passionateAboutBuildingAiToolsThat'),
      image: '/images/team/jihad.png',
      linkedin: 'https://www.linkedin.com/in/jihad-asad/'
    },
    {
      name: 'Mohammed Mansour',
      role: t('about.leadEngineer'),
      bio: t('about.anExpertInFullStackEngineering'),
      image: '/images/team/mohammed.png',
      linkedin: 'https://www.linkedin.com/in/mohammed-mansour-42a6b597/'
    }
  ];

  const scrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <BlogLayout>
      <SEO 
        title={t('about.aboutUsMeetTheTeamBehind')} 
        description={t('about.learnMoreAboutTheTeamBuilding')}
      />
      
      <div className="about-page">
        {/* HERO SECTION */}
        <section className="about-hero">
          <div className="lp-container">
            <div className="about-hero-content lp-reveal">
              <span className="lp-hero-badge">
                <span className="dot"></span>{' ' + t('about.ourStory')}
              </span>
              <h1>{t('about.empoweringLocalBusinesses')}<br/><span className="lp-gradient-text">{t('about.oneReviewAtATime')}</span></h1>
              <p>{t('about.reviioWasFoundedWithASimple')}</p>
            </div>
          </div>
        </section>

        {/* TEAM SECTION */}
        <section className="about-team-section">
          <div className="lp-container">
            <div className="about-section-header lp-reveal">
              <h2>{t('about.meetTheCoreTeam')}</h2>
              <p>{t('about.thePassionateMindsBehindTheAi')}</p>
            </div>
            
            <div className="about-team-grid">
              {team.map((member, index) => (
                <div key={index} className={`about-team-card lp-reveal lp-reveal-delay-${index + 1} ${index === 1 ? 'ceo-card-wide' : ''}`}>
                  <div className="team-card-image-wrapper">
                    <img src={member.image} alt={member.name} className="team-card-image" />
                    <div className="team-card-overlay"></div>
                  </div>
                  <div className="team-card-content">
                    <div className="team-card-header">
                      <h3 className="team-name">{member.name}</h3>
                      <a 
                        href={member.linkedin} 
                        target="_blank" 
                        rel="noopener noreferrer" 
                        className="team-linkedin"
                        aria-label={`${member.name} LinkedIn`}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"></path><rect x="2" y="9" width="4" height="12"></rect><circle cx="4" cy="4" r="2"></circle></svg>
                      </a>
                    </div>
                    <p className="team-role">{member.role}</p>
                    <p className="team-bio" dangerouslySetInnerHTML={{ __html: member.bio }}></p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      </div>
    </BlogLayout>
  );
}
